import React from 'react';
import { ChartLineIcon, TableIcon } from 'lucide-react';

import { Bloc } from '@mairistem/administres-utils';
import { Button, Dropdown } from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { baseUrlApi } from '@/routes';
import { useEntiteStore } from '@/zustand';
import { PARAM_NUMERISES } from '@/constants';

import BlocTableauStatistiques from './BlocTableauStatistiques';
import BlocGraphiqueStatistique from './BlocGraphiqueStatistique';

interface BlocStatistiquesProps {
  style?: React.CSSProperties;
}

const currentYear = new Date().getFullYear();

const NOMBRE_ANNEES = 10;

const optionsAnnees = (nombre: number) =>
  Array.from({ length: nombre }, (_, i) => {
    const year = currentYear - i;
    return { key: year, text: String(year), value: year };
  });

const yearOptions = optionsAnnees(NOMBRE_ANNEES);

const BlocStatistiques = (props: BlocStatistiquesProps) => {
  const { style } = props;

  const [modeTableau, setModeTableau] = React.useState(true);
  const [selectedYearMin, setSelectedYearMin] = React.useState(currentYear - 5);
  const [selectedYearMax, setSelectedYearMax] = React.useState(currentYear);
  // Année du tableau, distincte des bornes du graphique.
  const [anneeTableau, setAnneeTableau] = React.useState(currentYear);
  const [premiereAnnee, setPremiereAnnee] = React.useState<number | null>(null);
  const blocked = useEntiteStore((state) => state.blocked);

  // Les actes numérisés sont surtout anciens : le choix des années (tableau et
  // graphique) remonte jusqu'à la plus ancienne qui en compte.
  React.useEffect(() => {
    if (!blocked) {
      request
        .get(
          `${baseUrlApi}/numerisation-actes/statistiques/premiere-annee?${PARAM_NUMERISES}`,
        )
        .then((response: any) => setPremiereAnnee(response?.annee ?? null))
        .catch(() => setPremiereAnnee(null));
    }
  }, [blocked]);

  const annees = React.useMemo(
    () =>
      premiereAnnee && premiereAnnee < currentYear - NOMBRE_ANNEES + 1
        ? optionsAnnees(currentYear - premiereAnnee + 1)
        : yearOptions,
    [premiereAnnee],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need
  React.useEffect(() => {
    if (selectedYearMin > selectedYearMax) {
      const availableOptions = annees.filter(
        (option) => option.value <= selectedYearMax,
      );
      if (availableOptions.length > 0) {
        setSelectedYearMin(
          Math.min(...availableOptions.map((option) => option.value)),
        );
      }
    }
  }, [selectedYearMax]);

  const yearOptionsMin = React.useMemo(
    () => annees.filter((option) => option.value <= selectedYearMax),
    [annees, selectedYearMax],
  );

  const yearOptionsMax = React.useMemo(
    () => annees.filter((option) => option.value >= selectedYearMin),
    [annees, selectedYearMin],
  );

  const styleHeader = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5em',
    justifyContent: 'space-between',
    width: '100%',
    height: '2em',
  };

  // Statistiques sous forme de tableau
  if (modeTableau) {
    return (
      <Bloc
        icon="chart bar"
        header={
          <div style={styleHeader}>
            <div>
              <span>Statistiques sur l&apos;année </span>
              <Dropdown
                inline
                scrolling
                options={annees}
                value={anneeTableau}
                onChange={(_, data) => setAnneeTableau(data.value as number)}
                style={{ fontWeight: 600 }}
              />
            </div>
            <Button
              icon={<ChartLineIcon size="1em" />}
              onClick={() => setModeTableau(false)}
              compact
            />
          </div>
        }
        style={style}
      >
        <BlocTableauStatistiques selectedYear={anneeTableau} />
      </Bloc>
    );
  }

  // Statistiques sous forme de graphique
  return (
    <Bloc
      icon="chart bar"
      header={
        <div style={styleHeader}>
          <div>
            <span>Évolution du nombre d&apos;actes entre </span>
            <Dropdown
              inline
              scrolling
              options={yearOptionsMin}
              value={selectedYearMin}
              onChange={(_, data) => setSelectedYearMin(data.value as number)}
              style={{ fontWeight: 600 }}
            />
            <span>et </span>
            <Dropdown
              inline
              scrolling
              options={yearOptionsMax}
              value={selectedYearMax}
              onChange={(_, data) => setSelectedYearMax(data.value as number)}
              style={{ fontWeight: 600 }}
            />
          </div>
          <Button
            icon={<TableIcon size="1em" />}
            onClick={() => setModeTableau(true)}
            compact
          />
        </div>
      }
      style={style}
    >
      <BlocGraphiqueStatistique
        anneeMin={selectedYearMin}
        anneeMax={selectedYearMax}
      />
    </Bloc>
  );
};

export default BlocStatistiques;
